import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi2';
import { useLocation, useNavigate } from 'react-router';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { IBaseAction } from '@models/actions/IBaseAction';
import { AttachActionTypes } from '@models/actions/AttachActionTypes';
import { IDocBasedService } from '@services/actions/IDocBasedService';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import Menu from '@srm/ui/menu';
import { convertActions } from '@utils/actionsHelpers';
import { useDocumentHashContext } from '@hooks/useDocumentHashContext';
import { useDraftMonitorContext } from '@hooks/useDraftMonitorContext';
import { ConfigService } from '@/configuration/services/configService';
import { IDebugSettings } from '@models/IDebugSettings';

interface AttachMoreActionsProps {
    service: AttachActionsService;
    /** Запятыми склеенные ключи выбранных аттачей. */
    objIds: string;
    objData: unknown[];
    onModifyData?: () => void;
}

// «Сохранить/Скачать» вынесено отдельным чипом — в меню «Ещё» его не дублируем.
const DOWNLOAD_TYPE = AttachActionTypes[AttachActionTypes.Download];

/** Меню групповых действий «Ещё»: лениво грузит действия с бэка по выбранным
 *  аттачам (кроме скачивания). Стиль — как у чипа «Скачать». */
const AttachMoreActions: FC<AttachMoreActionsProps> = ({ service, objIds, objData, onModifyData }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const config = ConfigService.get();
    const [debugSettings] = useLocalStorageState<IDebugSettings>('Debug-Settings', {
        notifyFormulaErrors: config.environment !== 'PROD',
        showActionKeys: config.environment !== 'PROD',
    });
    const draftMonitor = useDraftMonitorContext();
    const docHashServ = useDocumentHashContext();

    const [isLoading, setIsLoading] = useState(false);
    const [fetchedActions, setFetchedActions] = useState<IBaseAction[]>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [element, setElement] = useState<ReactElement>();

    // Смена выбора (или режима ключей) инвалидирует загруженные действия.
    useEffect(() => {
        setFetchedActions(undefined);
        setIsDialogOpen(false);
    }, [objIds, debugSettings?.showActionKeys]);

    const onActionClick = useCallback(
        (action: IBaseAction) => {
            const executor = service.getActionExecutor(action);
            if (executor) {
                executor.navigate = navigate;
                executor.location = location;
            }
            const parentId = (service as unknown as IDocBasedService).documentId;

            executor?.run(
                objIds,
                parentId,
                action,
                objData,
                (isSucceed) => {
                    draftMonitor?.resume();
                    if (docHashServ) docHashServ.resumeMonitoring();
                    setElement(undefined);
                    if (isSucceed && onModifyData) onModifyData();
                },
                () => {
                    draftMonitor?.pause();
                    if (docHashServ) docHashServ.pause();
                    setElement(executor?.visualElement && executor.visualElement());
                },
            );
            setElement(executor?.visualElement && executor.visualElement());
        },
        [docHashServ, draftMonitor, location, navigate, objData, objIds, onModifyData, service],
    );

    const actItems = useMemo(() => {
        const filtered = fetchedActions?.filter((a) => a.type !== DOWNLOAD_TYPE);
        return convertActions(filtered, onActionClick, debugSettings.showActionKeys, config);
    }, [config, debugSettings.showActionKeys, fetchedActions, onActionClick]);

    const loadActions = useCallback(() => {
        if (fetchedActions || isLoading) return;
        setIsLoading(true);
        void service
            .fetchGroupActions(objIds)
            .then((res) => setFetchedActions(res.data))
            .finally(() => setIsLoading(false));
    }, [fetchedActions, isLoading, objIds, service]);

    const onOpenChange = (opened: boolean) => {
        setIsDialogOpen(opened);
        if (opened) loadActions();
    };

    return (
        <>
            <Menu
                list={actItems}
                loading={isLoading}
                opened={isDialogOpen}
                onOpenChange={onOpenChange}
                position="bottom-start"
                width={320}
                popoverProps={{ className: 'group-action-button__dialog', hoverEndAdornment: true }}
            >
                <button type="button" className="atb__chip" aria-label="Ещё действия">
                    <HiChevronDown aria-hidden />
                    Ещё
                </button>
            </Menu>
            {element}
        </>
    );
};

export default AttachMoreActions;
